<script lang="ts">
    import Chart from 'chart.js/auto'
    import ChartDataLabels from 'chartjs-plugin-datalabels'
    // @ts-ignore - funnel-graph-js não publica types
    import FunnelGraph from 'funnel-graph-js/dist/js/funnel-graph.js'
    import 'funnel-graph-js/dist/css/main.min.css'
    import 'funnel-graph-js/dist/css/theme.min.css'
    import { onMount, tick } from 'svelte/internal'
    import type { Person } from '../../modules/eventsApi/persons/person'
    import { PersonsRepository } from '../../modules/eventsApi/persons/persons.repository'
    import { PersonsService } from '../../modules/eventsApi/persons/persons.service'
    import type { Referral } from '../../modules/eventsApi/referrals/referral'
    import type { Invoice } from '../../modules/posApi/invoices/invoice'
    import { InvoicesRepository } from '../../modules/posApi/invoices/invoices.repository'
    import { InvoicesService } from '../../modules/posApi/invoices/invoices.service'
    import { ServicesRepository } from '../../modules/posApi/services/services.repository'
    import { ServicesService } from '../../modules/posApi/services/services.service'
    import { AppointmentsRepository } from '../../modules/schedulerApi/appointments/appointments.repository'
    import { AppointmentsService } from '../../modules/schedulerApi/appointments/appointments.service'
    import DateSelect from '../_common/date-select.svelte'
    import InlineSelect from '../_common/inline-select.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import Zact from '../_common/z/zact.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import Zframe from '../_common/z/zframe.svelte'

    const rInvoices = new InvoicesRepository()
    const sInvoices = new InvoicesService(rInvoices)

    let rAppointments = new AppointmentsRepository()
    let sAppointments = new AppointmentsService(rAppointments)

    let personsR = new PersonsRepository()
    let personsS = new PersonsService(personsR)

    let servicesR = new ServicesRepository()
    let servicesS = new ServicesService(servicesR)

    let dates: { from: Date; to: Date } = window.api.utils.dates.currentMonth()

    let containerFunil: HTMLDivElement
    let containerNoShow: HTMLCanvasElement
    let containerConversion: HTMLCanvasElement
    let containerParticipacao: HTMLCanvasElement
    let containerRadar: HTMLCanvasElement
    let containerValues: HTMLCanvasElement
    let show

    let charts: Chart[] = []
    const MUTED = '#666'
    const GRID = '#555'
    const PALETTE = ['#008FFB', '#379E75', '#FEB019', '#775DD0', '#6179F3', '#D0483E', '#9AA0A6']

    // "Do cliente" usa a origem do cadastro da pessoa (person.referralId); "Da venda" usa a origem escolhida
    // no PDV (invoice.referralId). Funil, presença e conversão partem de agendamentos, que não têm venda —
    // logo não têm origem de venda para classificá-los. Por isso esses 3 só existem na visão por cliente.
    let originBy: 'person' | 'invoice' = 'person'
    const originOptions = [
        { label: 'Do cliente', value: 'person' },
        { label: 'Da venda', value: 'invoice' },
    ]
    // chave sintética para vendas sem origem (ou com origem que foi excluída do cadastro de origens)
    const NOT_INFORMED = '__nao_informado__'

    $: originHint =
        originBy === 'invoice'
            ? ' Considera a origem informada em cada venda; vendas sem origem aparecem como "Não informado".'
            : ' Considera a origem do cadastro do cliente.'

    function dateChanged(event) {
        dates = event.detail
        getData()
    }

    async function getData() {
        try {
            show = true
            if (originBy === 'invoice') {
                await loadByInvoice()
            } else {
                await loadByPerson()
            }
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    // visão "Da venda": só as vendas do período e o cadastro de origens — sem agendamentos, pessoas ou janela de conversão
    async function loadByInvoice() {
        const [invoices, allReferrals]: [Invoice[], Referral[]] = await Promise.all([
            sInvoices.getAll({
                from: dates.from,
                to: dates.to,
                service: true,
                status: ['closed', 'subsidized', 'partial', 'open', 'credit'],
            }),
            window.api.events.referrals.getAll(),
        ])

        // o cadastro de origens não devolve as excluídas; venda apontando para uma delas também vai para "Não informado"
        const knownIds = new Set(allReferrals.map((r) => r.referralId))
        const keyOf = (invoice: Invoice) => (invoice.referralId && knownIds.has(invoice.referralId) ? invoice.referralId : NOT_INFORMED)
        const referrals = [...allReferrals, { referralId: NOT_INFORMED, label: 'Não informado' } as Referral]
        console.log('data', invoices, referrals)

        charts.forEach((c) => c.destroy())
        charts = []
        // os frames de funil/presença/conversão saem da tela nesse modo; espera o DOM assentar antes de desenhar
        await tick()

        generateParticipacao(invoices, referrals, keyOf)
        generateRadar(invoices, referrals, keyOf)
        generateValues(invoices, referrals, keyOf)
    }

    async function loadByPerson() {
        let invoices = []
        let futureInvoices = []
        let persons: Person[] = []
        let referrals: Referral[] = []

        // get invoices
        invoices = await sInvoices.getAll({
            from: dates.from,
            to: dates.to,
            service: true,
            status: ['closed', 'subsidized', 'partial', 'open', 'credit'],
        })
        // get future invoices (conversion window)
        let from = new Date(dates.from)
        from.setDate(from.getDate() + 1)
        let to = new Date(dates.to)
        to.setDate(to.getDate() + 31)
        futureInvoices = await sInvoices.getAll({
            from: from,
            to: to,
            service: true,
            status: ['closed', 'subsidized', 'partial', 'open', 'credit'],
        })
        // get consultings
        const services = await servicesS.getAll({ consulting: true })
        let consultingServicesId = []
        for (const service of services) {
            consultingServicesId.push(service.serviceId)
        }
        const consultings = await sAppointments.getConsulting(dates.from, dates.to, consultingServicesId)
        // get persons
        persons = []
        let personsId = []
        for (const invoice of invoices) {
            personsId.push(invoice.personId)
        }
        for (const invoice of futureInvoices) {
            personsId.push(invoice.personId)
        }
        for (const appointment of consultings) {
            personsId.push(appointment.personId)
        }
        personsId = personsId.filter((v, i, a) => a.indexOf(v) === i)
        if (personsId.length > 0) {
            for (let i = 0; i < personsId.length; i += 100) {
                const chunk = personsId.slice(i, i + 100)
                persons = persons.concat(await personsS.getAll({ persons: chunk.join(',') }))
            }
        }
        // hidrating (hidratar consultings / buscar 30 dias àfrente de vendas para ver conversões futuras)
        for (let invoice of invoices) {
            invoice.person = persons.find((el) => el.personId === invoice.personId)
        }
        for (let invoice of futureInvoices) {
            invoice.person = persons.find((el) => el.personId === invoice.personId)
        }
        for (let consulting of consultings) {
            consulting.person = persons.find((el) => el.personId === consulting.personId)
        }
        // get referrals
        invoices.forEach((el) => {
            if (el.person?.referral) referrals.push(el.person?.referral)
        })
        futureInvoices.forEach((el) => {
            if (el.person?.referral) referrals.push(el.person?.referral)
        })
        consultings.forEach((el) => {
            if (el.person?.referral) referrals.push(el.person?.referral)
        })
        referrals = removeDuplicates(referrals, 'referralId')
        console.log('data', invoices, futureInvoices, consultings, referrals)

        const keyOf = (invoice: Invoice) => invoice.person?.referralId

        charts.forEach((c) => c.destroy())
        charts = []
        // voltando de "Da venda", os frames de funil/presença/conversão acabaram de ser recriados
        await tick()

        generateFunil(invoices, futureInvoices, consultings, referrals)
        generateNoShow(consultings, referrals)
        generateConversions(invoices, futureInvoices, consultings, referrals)
        generateParticipacao(invoices, referrals, keyOf)
        generateRadar(invoices, referrals, keyOf)
        generateValues(invoices, referrals, keyOf)
    }

    function removeDuplicates(myArr, prop) {
        return myArr.filter((obj, pos, arr) => {
            return arr.map((mapObj) => mapObj[prop]).indexOf(obj[prop]) === pos
        })
    }

    function currency(val: number) {
        return window.configs.currency + Number(val || 0).toFixed(2)
    }

    // funil consolidado, segmentado por origem (biblioteca funnel-graph-js, não Chart.js).
    // cada segmento usa a mesma cor nas 3 etapas, então dá pra acompanhar visualmente onde cada
    // origem perde gente no funil (agendou mas não veio, veio mas não converteu)
    async function generateFunil(invoices, futureInvoices, consultings, referrals: Referral[]) {
        const totalInvoices = invoices.concat(futureInvoices)

        let rows = referrals
            .map((referral) => {
                const refConsultingsAll = consultings.filter((el) => el.person?.referralId === referral?.referralId && el.status !== 'agendamento_excluido')
                const refConsultingsShow = refConsultingsAll.filter((el) => el.status === 'agendamento_compareceu')
                const showPersonIds = refConsultingsShow.map((el) => el.personId)
                const convertido = removeDuplicates(
                    totalInvoices.filter((el) => showPersonIds.includes(el.personId)),
                    'personId',
                ).length
                return { label: referral.label, agendado: refConsultingsAll.length, compareceu: refConsultingsShow.length, convertido }
            })
            .filter((r) => r.agendado > 0)
            .sort((a, b) => b.agendado - a.agendado)

        containerFunil.innerHTML = ''
        if (rows.length === 0) return

        const colors = rows.map((_, i) => PALETTE[i % PALETTE.length])

        const graph = new FunnelGraph({
            container: '#origens-funil',
            direction: 'horizontal',
            gradientDirection: 'horizontal',
            displayPercent: true,
            data: {
                labels: ['Agendado', 'Compareceu', 'Converteu'],
                subLabels: rows.map((r) => r.label),
                colors,
                values: [rows.map((r) => r.agendado), rows.map((r) => r.compareceu), rows.map((r) => r.convertido)],
            },
        })
        graph.draw()
    }

    async function generateNoShow(consultings, referrals: Referral[]) {
        let rows = referrals.map((referral) => {
            const refConsultings = consultings.filter((el) => el.person?.referralId === referral?.referralId)
            const total = refConsultings.filter((el) => el.status !== 'agendamento_excluido').length
            const show = refConsultings.filter((el) => el.status === 'agendamento_compareceu').length
            return { label: referral.label, percent: total === 0 ? 0 : Number(((100 / total) * show).toFixed(0)) }
        })
        rows.sort((a, b) => b.percent - a.percent)

        const chart = new Chart(containerNoShow, {
            type: 'bar',
            data: {
                labels: rows.map((r) => r.label),
                datasets: [{ label: 'Presença', data: rows.map((r) => r.percent), backgroundColor: '#008FFB', borderRadius: 4 }],
            },
            options: {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                // reserva espaço à direita da área de plotagem para o datalabel da barra mais longa não ficar cortado
                layout: { padding: { right: 40 } },
                plugins: {
                    legend: { display: false },
                    tooltip: { callbacks: { label: (ctx) => ctx.parsed.x + '%' } },
                    datalabels: { anchor: 'end', align: 'end', color: MUTED, formatter: (val) => val + '%' },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED } },
                    y: { grid: { display: false }, ticks: { color: MUTED } },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    async function generateConversions(invoices, futureInvoices, consultings, referrals: Referral[]) {
        const totalInvoices = invoices.concat(futureInvoices)
        let rows = referrals.map((referral) => {
            const perConsultings = consultings.filter((el) => el.person?.referralId === referral?.referralId).map((el) => el.personId)
            const consulting = perConsultings.length
            const invoice = removeDuplicates(
                totalInvoices.filter((el) => el.person?.referralId === referral?.referralId && perConsultings.includes(el.personId)),
                'personId',
            ).length
            return { label: referral.label, percent: consulting === 0 ? 0 : Number(((100 / consulting) * invoice).toFixed(0)) }
        })
        rows.sort((a, b) => b.percent - a.percent)

        const chart = new Chart(containerConversion, {
            type: 'bar',
            data: {
                labels: rows.map((r) => r.label),
                datasets: [{ label: 'Conversão', data: rows.map((r) => r.percent), backgroundColor: '#FEB019', borderRadius: 4 }],
            },
            options: {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                // reserva espaço à direita da área de plotagem para o datalabel da barra mais longa não ficar cortado
                layout: { padding: { right: 40 } },
                plugins: {
                    legend: { display: false },
                    tooltip: { callbacks: { label: (ctx) => ctx.parsed.x + '%' } },
                    datalabels: { anchor: 'end', align: 'end', color: MUTED, formatter: (val) => val + '%' },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED } },
                    y: { grid: { display: false }, ticks: { color: MUTED } },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    async function generateParticipacao(invoices, referrals: Referral[], keyOf: (invoice: Invoice) => string) {
        let rows = referrals.map((referral) => ({
            label: referral.label,
            total: invoices.filter((el) => keyOf(el) === referral?.referralId).reduce((total, el) => total + el.total, 0),
        }))
        rows = rows.filter((r) => r.total > 0).sort((a, b) => b.total - a.total)
        const totalGeral = rows.reduce((t, r) => t + r.total, 0)

        const chart = new Chart(containerParticipacao, {
            type: 'doughnut',
            data: {
                labels: rows.map((r) => r.label),
                datasets: [
                    {
                        data: rows.map((r) => (totalGeral > 0 ? Number(((100 / totalGeral) * r.total).toFixed(1)) : 0)),
                        backgroundColor: rows.map((_, i) => PALETTE[i % PALETTE.length]),
                        borderColor: 'transparent',
                        borderWidth: 2,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: 'top', labels: { color: MUTED } },
                    tooltip: { callbacks: { label: (ctx) => ctx.label + ': ' + ctx.parsed.toFixed(1) + '%' } },
                    datalabels: { color: '#fff', font: { size: 11, weight: 'bold' }, formatter: (val) => (val >= 5 ? val.toFixed(1) + '%' : '') },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    async function generateRadar(invoices, referrals: Referral[], keyOf: (invoice: Invoice) => string) {
        let rows = referrals.map((referral) => {
            const fromRef = invoices.filter((el) => keyOf(el) === referral?.referralId)
            const qtde = fromRef.length
            const valor = fromRef.reduce((total, el) => total + el.total, 0)
            return { key: referral.referralId, label: referral.label, ticket: qtde === 0 ? 0 : valor / qtde, quantidade: qtde, valor }
        })
        // "Não informado" mistura origens diferentes, então não faz sentido compará-lo como se fosse uma origem
        rows = rows
            .filter((r) => r.valor > 0 && r.key !== NOT_INFORMED)
            .sort((a, b) => b.valor - a.valor)
            .slice(0, 5)
        if (rows.length === 0) return

        const normalize = (values: number[]) => {
            const max = Math.max(...values, 1)
            return values.map((v) => Number(((v / max) * 100).toFixed(1)))
        }

        const chart = new Chart(containerRadar, {
            type: 'radar',
            data: {
                labels: rows.map((r) => r.label),
                datasets: [
                    {
                        label: 'Ticket médio',
                        data: normalize(rows.map((r) => r.ticket)),
                        borderColor: '#008FFB',
                        backgroundColor: 'rgba(0,143,251,0.15)',
                        pointRadius: 3,
                        borderWidth: 2,
                    },
                    {
                        label: 'Quantidade',
                        data: normalize(rows.map((r) => r.quantidade)),
                        borderColor: '#FEB019',
                        backgroundColor: 'rgba(254,176,25,0.15)',
                        pointRadius: 3,
                        borderWidth: 2,
                    },
                    {
                        label: 'Valor total',
                        data: normalize(rows.map((r) => r.valor)),
                        borderColor: '#379E75',
                        backgroundColor: 'rgba(55,158,117,0.15)',
                        pointRadius: 3,
                        borderWidth: 2,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: 'top', labels: { color: MUTED } },
                    tooltip: { callbacks: { label: (ctx) => ctx.dataset.label + ': ' + ctx.parsed.r.toFixed(0) + ' / 100' } },
                },
                scales: {
                    r: {
                        min: 0,
                        max: 100,
                        ticks: { display: false, backdropColor: 'transparent' },
                        grid: { color: GRID },
                        angleLines: { color: GRID },
                        pointLabels: { color: MUTED },
                    },
                },
            },
        })
        charts.push(chart)
    }

    async function generateValues(invoices, referrals: Referral[], keyOf: (invoice: Invoice) => string) {
        let rows = referrals.map((referral) => ({
            label: referral.label,
            total: invoices.filter((el) => keyOf(el) === referral?.referralId).reduce((total, el) => total + el.total, 0),
        }))
        rows = rows.filter((r) => r.total > 0).sort((a, b) => b.total - a.total)

        const chart = new Chart(containerValues, {
            type: 'bar',
            data: {
                labels: rows.map((r) => r.label),
                datasets: [{ label: 'Valor vendido', data: rows.map((r) => r.total), backgroundColor: '#379E75', borderRadius: 4 }],
            },
            options: {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                // reserva espaço à direita da área de plotagem para o datalabel da barra mais longa não ficar cortado
                layout: { padding: { right: 60 } },
                plugins: {
                    legend: { display: false },
                    tooltip: { callbacks: { label: (ctx) => currency(ctx.parsed.x) } },
                    datalabels: { anchor: 'end', align: 'end', color: MUTED, formatter: (val) => currency(val) },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED } },
                    y: { grid: { display: false }, ticks: { color: MUTED } },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    function evaluateError(error) {
        window.error(error)
    }

    onMount(async () => {
        await getData()
    })
</script>

<div class="base-dash sales-dash">
    <Zact>
        <DateSelect startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
        <InlineSelect class="limit-w_400" label="Origem" bind:value={originBy} options={originOptions} on:change={getData} />
        <div class="spacer"></div>
    </Zact>

    <ZContainer class="h-space">
        {#if originBy === 'person'}
            <Zframe
                size={12}
                title="Funil de conversão"
                headline="Agendamentos, comparecimentos e conversões (venda em até 31 dias) no período, segmentados por origem — cada cor acompanha a mesma origem nas 3 etapas, mostrando onde cada uma perde gente no caminho."
            >
                <div class="chart-box" id="origens-funil" style="height:440px" bind:this={containerFunil}></div>
            </Zframe>

            <Zframe
                size={6}
                sizeM={12}
                title="Taxa de presença por origem"
                headline="Taxa de comparecimento para cada origem de clientes com base nas informações da agenda e do cadastro de origem do cliente."
            >
                <div class="chart-box chart-box_tall">
                    <canvas bind:this={containerNoShow}></canvas>
                </div>
            </Zframe>

            <Zframe
                size={6}
                sizeM={12}
                title="Taxa de conversão por origem"
                headline="Taxa de conversão do cliente para cada origem distinta. Útil para identificar as melhores origens de clientes."
            >
                <div class="chart-box chart-box_tall">
                    <canvas bind:this={containerConversion}></canvas>
                </div>
            </Zframe>
        {/if}

        <Zframe size={6} sizeM={12} title="Participação no valor vendido" headline={'Percentual do valor total vendido no período que cada origem representa.' + originHint}>
            <div class="chart-box chart-box_tall">
                <canvas bind:this={containerParticipacao}></canvas>
            </div>
        </Zframe>

        <Zframe
            size={6}
            sizeM={12}
            title="Comparativo entre origens"
            headline={'Compara ticket médio, quantidade e valor vendido das 5 origens com maior faturamento. Os valores são normalizados (0 a 100) para comparar métricas de escalas diferentes.' +
                originHint}
        >
            <div class="chart-box chart-box_tall">
                <canvas bind:this={containerRadar}></canvas>
            </div>
        </Zframe>

        <Zframe size={12} title="Valor vendido por origem" headline={'Valor total vendido por cada origem.' + originHint}>
            <div class="chart-box chart-box_tall">
                <canvas bind:this={containerValues}></canvas>
            </div>
        </Zframe>
    </ZContainer>
</div>
<LoadSpinner delay={300} {show} />

<style>
    .chart-box {
        position: relative;
        width: 100%;
        height: 320px;
    }
    .chart-box_tall {
        height: 360px;
    }

    :global(.theme_light #origens-funil .label__value),
    :global(.theme_light #origens-funil .svg-funnel-js__subLabel) {
        color: #212529;
    }
    :global(.theme_light #origens-funil .label__title) {
        color: #0f9d67;
    }
    :global(.theme_light #origens-funil .label__percentage) {
        color: #5b52a3;
    }
</style>
